import React from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../Home/Components/Navbar/Navbar";
import Footer from "../../GlobalComp/Footer/Footer";
import Review from "../Home/Sections/Review/Review";
import Faq from "../Home/Sections/Faq/Faq";
import ServiceAreas from "./ServiceAreas";
import { HERO_SRC, HERO_SRCSET, HERO_SIZES } from "../../../utils/heroImage";
import { useGoogleRating, formatRating } from "../../../utils/googleRating";
import Classes from "./ServicePage.module.css";
import {
    SITE_URL,
    BUSINESS_NAME,
    BOOKING_URL,
    PHONE_DISPLAY,
    PHONE_TEL,
} from "./serviceData";


function buildSchema(page) {
    const url = `${SITE_URL}${page.path}`;
    const crumbs = [{ name: "Home", path: "/" }];
    if (page.parent) crumbs.push({ name: page.parent.label, path: page.parent.path });
    crumbs.push({ name: page.city || page.serviceName, path: page.path });

    return [
        {
            "@context": "https://schema.org",
            "@type": "Service",
            name: page.city ? `${page.serviceName} in ${page.city}, FL` : page.serviceName,
            serviceType: page.serviceName,
            url,
            description: page.description,
            provider: { "@id": `${SITE_URL}/#business`, "@type": "HomeAndConstructionBusiness", name: BUSINESS_NAME },
            areaServed: page.city
                ? { "@type": "City", name: `${page.city}, FL` }
                : [
                  { "@type": "AdministrativeArea", name: "Palm Beach County, FL" },
                  { "@type": "AdministrativeArea", name: "Broward County, FL" },
              ],
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: crumbs.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.name,
                item: `${SITE_URL}${c.path}`,
            })),
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: page.faqs.map((f) => ({
                "@type": "Question",
                name: f.question,
                acceptedAnswer: { "@type": "Answer", text: f.answer },
            })),
        },
    ];
}

function Stars() {
    return (
        <span className={Classes.stars} aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
                <svg key={i} viewBox="0 0 20 20">
                    <path d="M10 1.5l2.6 5.3 5.9.9-4.25 4.1 1 5.8L10 14.9l-5.25 2.7 1-5.8L1.5 7.7l5.9-.9z" />
                </svg>
            ))}
        </span>
    );
}

export default function ServicePage({ page }) {
    const googleRating = useGoogleRating();
    const url = `${SITE_URL}${page.path}`;

    return (
        <div className={Classes.page}>
            <Helmet>
                <title>{page.title}</title>
                <meta name="description" content={page.description} />
                <link rel="canonical" href={url} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={page.title} />
                <meta property="og:description" content={page.description} />
                <meta property="og:url" content={url} />
                <script type="application/ld+json">{JSON.stringify(buildSchema(page))}</script>
            </Helmet>

            <Navbar />

            {/* ===== Hero ===== */}
            <header className={Classes.hero}>
                <img src={HERO_SRC} srcSet={HERO_SRCSET} sizes={HERO_SIZES} alt="" className={Classes.heroBg} draggable={false} fetchpriority="high" decoding="async" />
                <div className={Classes.heroOverlay} />
                <div className={Classes.heroGrid}>
                    <div className={Classes.heroText}>
                        <p className={Classes.eyebrow}>{page.eyebrow}</p>
                        <h1>{page.h1}</h1>
                        <p className={Classes.heroIntro}>{page.intro}</p>
                        <ul className={Classes.trustRow}>
                            <li>
                                <Stars />
                                <span>{formatRating(googleRating.rating)} on Google</span>
                            </li>
                            <li>Licensed and insured</li>
                            <li>Based in Boca Raton</li>
                        </ul>
                    </div>

                    <aside className={Classes.bookCard}>
                        <h2>{page.bookingCard.heading}</h2>
                        <ul>
                            {page.bookingCard.points.map((p) => (
                                <li key={p}>{p}</li>
                            ))}
                        </ul>
                        <a className={Classes.primaryBtn} href={BOOKING_URL} target="_blank" rel="noreferrer">
                            Book an appointment online
                        </a>
                        <a className={Classes.ghostBtn} href={PHONE_TEL}>
                            Call {PHONE_DISPLAY}
                        </a>
                    </aside>
                </div>
            </header>

            {/* ===== Highlights ===== */}
            <section className={Classes.band}>
                <div className={Classes.inner}>
                    <div className={Classes.sectionHead}>
                        <p className={Classes.kicker}>{page.serviceName}</p>
                        <h2>{page.highlights.heading}</h2>
                        {page.highlights.lead.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </div>
                    <div className={Classes.tileGrid}>
                        {page.highlights.items.map((item) => (
                            <article key={item.title} className={Classes.tile}>
                                <span className={Classes.tileIcon}>
                                    <img src={item.icon} alt="" draggable={false} loading="lazy" />
                                </span>
                                <h3>{item.title}</h3>
                                <p>{item.desc}</p>
                            </article>
                        ))}
                    </div>
                    <div className={Classes.sectionCta}>
                        <a className={Classes.primaryBtn} href={BOOKING_URL} target="_blank" rel="noreferrer">
                            {page.highlights.cta}
                        </a>
                    </div>
                </div>
            </section>

            {/* ===== Checklists (service pages only) ===== */}
            {page.listBlocks.length > 0 ? (
                <section className={`${Classes.band} ${Classes.bandGray} ${Classes.bandChecklist}`}>
                    <div className={`${Classes.inner} ${Classes.sectionHead}`}>
                        <p className={Classes.kicker}>{page.checklistHead.kicker}</p>
                        <h2>{page.checklistHead.heading}</h2>
                        <p>{page.checklistHead.lead}</p>
                    </div>
                    <div className={`${Classes.inner} ${Classes.listGrid} ${page.listBlocks.length === 1 ? Classes.listGridSingle : ""}`}>
                        {page.listBlocks.map((block, i) => (
                            <section key={block.heading} className={Classes.listCard}>
                                <div className={Classes.listHead}>
                                    <span className={Classes.listIcon} aria-hidden="true">
                                        {i === 0 ? (
                                            <svg viewBox="0 0 24 24">
                                                <path d="M12 2 1 21h22L12 2zm0 6 .01.01L13 15h-2l.99-6.99zM11 17h2v2h-2v-2z" />
                                            </svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24">
                                                <path d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z" />
                                            </svg>
                                        )}
                                    </span>
                                    <h2>{block.heading}</h2>
                                </div>
                                <ul>
                                    {block.items.map((item) => (
                                        <li key={item}>{item}</li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                    <div className={Classes.sectionCta}>
                        <a className={Classes.primaryBtn} href={BOOKING_URL} target="_blank" rel="noreferrer">
                            {page.checklistHead.cta}
                        </a>
                    </div>
                </section>
            ) : null}

            {/* ===== How it works ===== */}
            <section className={`${Classes.band} ${Classes.bandCompact} ${page.listBlocks.length === 0 ? Classes.bandGray : ""}`}>
                <div className={Classes.inner}>
                    <div className={Classes.sectionHead}>
                        <p className={Classes.kicker}>How It Works</p>
                        <h2>Booked, done, and tested in four steps</h2>
                        <p>{page.stepsLead}</p>
                    </div>
                    <ol className={Classes.steps}>
                        {page.steps.map((step, i) => (
                            <li key={step.title} className={Classes.step}>
                                <span className={Classes.stepNum}>{i + 1}</span>
                                <h3>{step.title}</h3>
                                <p>{step.desc}</p>
                            </li>
                        ))}
                    </ol>
                    <div className={Classes.sectionCta}>
                        <a className={Classes.primaryBtn} href={BOOKING_URL} target="_blank" rel="noreferrer">
                            Get started today
                        </a>
                    </div>
                </div>
            </section>

            {/* ===== Reviews ===== */}
            <Review />

            {/* ===== Service areas ===== */}
            {page.showCities && <ServiceAreas />}

            {/* ===== FAQ (same component and design as the homepage) ===== */}
            <Faq items={page.faqs} withSchema={false} />

            <Footer />
        </div>
    );
}
